import { SETTINGS_CATALOG } from './settings-catalog'

/**
 * The top bar's breadcrumb trail.
 *
 * Openings and Engage each collapsed to a single nav leaf, with the leaves they used to hold now
 * tabs on the screen. That makes the bar the only place the hierarchy is still written down, so it
 * shows section › tab rather than one flat title. A record (an editor) is shorter still: the view
 * it came from and its own name.
 *
 * The landing tab collapses into the section name — /jobs/manage reads "Openings", not
 * "Openings › Manage" — because a crumb that repeats its parent tells you nothing. A non-landing tab
 * keeps its section ahead of it (Openings › Settings).
 *
 * Pure and table-driven so it is unit-testable without the router.
 */
export interface Crumb {
  label: string
  /** Absent on the last crumb: it is the current page, not somewhere to go. */
  link?: string
  queryParams?: Record<string, string>
}

interface SectionDef {
  label: string
  landing: string
  landingQueryParams?: Record<string, string>
  /** Longest-prefix wins, so /jobs/manage is tested before /jobs. */
  tabs: { prefix: string; label: string; isLanding?: boolean }[]
}

const OPENINGS: SectionDef = {
  label: 'Openings',
  landing: '/jobs/manage',
  tabs: [
    { prefix: '/jobs/manage', label: 'Manage', isLanding: true },
    { prefix: '/jobs/template', label: 'Templates' },
    { prefix: '/jobs/profile', label: 'Templates' },
    { prefix: '/jobs/settings', label: 'Settings' },
  ],
}

const ENGAGE: SectionDef = {
  label: 'Pipelines',
  landing: '/jobs/list',
  tabs: [
    { prefix: '/jobs/list', label: 'Pipelines', isLanding: true },
    { prefix: '/jobs/applications', label: 'Applicant Search' },
    { prefix: '/candidate-pool', label: 'Talent Pool' },
    { prefix: '/engagement/prospects', label: 'Prospects' },
    { prefix: '/engagement/settings', label: 'Settings' },
  ],
}

/**
 * My Account (CNCT-6812). The details are tabs on one screen, the same as the two sections
 * above, so the bar names the tab you are on rather than saying "My Account" on all six.
 * Personal Information is the landing tab, so it collapses into the section name.
 */
const ACCOUNT: SectionDef = {
  label: 'My Account',
  landing: '/account',
  tabs: [
    { prefix: '/account/hiring-team-settings', label: 'Decision Team Settings' },
    { prefix: '/account/profile-pic', label: 'Profile Picture' },
    { prefix: '/account/profile-video', label: 'Profile Video' },
    { prefix: '/account/security', label: 'Security' },
    { prefix: '/account/notifications', label: 'Notifications' },
    // Last, and shortest: longest-prefix wins, so this only matches /account itself.
    { prefix: '/account', label: 'Personal Information', isLanding: true },
  ],
}

/**
 * Company Settings. Every /settings screen is reached from the hub, so the bar names the screen
 * under the hub (Company Settings › Departments) instead of each screen's own long route title.
 * The labels are the hub cards' own, read from the catalog so the two never drift; a screen's
 * sub-pages (a department's detail) match by prefix and keep the screen's name.
 */
/** Hub screens that kept a route outside /settings. */
const COMPANY_SETTINGS_ELSEWHERE = ['/users', '/customer-clients']

const COMPANY_SETTINGS: SectionDef = {
  label: 'Company Settings',
  landing: '/settings',
  tabs: [
    ...SETTINGS_CATALOG.filter(cat => cat.route.startsWith('/settings/') || COMPANY_SETTINGS_ELSEWHERE.includes(cat.route)).map(cat => ({
      prefix: cat.route,
      label: cat.label,
    })),
    { prefix: '/settings', label: 'Company Settings', isLanding: true },
  ],
}

/**
 * Record routes and the one screen each logically comes back to. A record shows at most one crumb
 * ahead of its own name (Openings › Senior Nurse), not the section › tab it sits under: the trail
 * is a way back out, and one crumb that goes straight there says it plainest. A page that knows
 * better where it was reached from (a candidate's opening) passes its own `back` instead.
 *
 * The page publishes its record once it has loaded (see RecordTitleService). Until then the trail
 * is empty rather than a placeholder that then changes under you. `static` marks a record route
 * whose page publishes nothing, so it shows the route's own title straight away.
 *
 * Longest-prefix wins; `except` keeps a list route that shares the record's prefix
 * (/jobs/template/list) from being read as a record.
 */
const RECORDS: { prefix: string; except?: string[]; back: Crumb; static?: boolean }[] = [
  { prefix: '/jobs/post', back: { label: OPENINGS.label, link: OPENINGS.landing } },
  { prefix: '/jobs/template/', except: ['/jobs/template/list'], back: { label: 'Templates', link: '/jobs/template/list' } },
  { prefix: '/jobs/profile/', except: ['/jobs/profile/list'], back: { label: 'Templates', link: '/jobs/template/list' } },
  { prefix: '/jobs/candidates', back: { label: ENGAGE.label, link: ENGAGE.landing } },
  { prefix: '/jobs/candidates/detail/core', back: { label: ENGAGE.label, link: ENGAGE.landing }, static: true },
  { prefix: '/campaigns/opening/', back: { label: 'Campaigns', link: '/campaigns' } },
]

/** What a record page publishes for the trail once it has loaded. */
export interface RecordCrumbs {
  /** The record's own name — an opening's or template's title, a candidate's name. */
  title: string | null
  /** Where the record was reached from, when only the page knows (a candidate's opening). */
  back?: Crumb
}

const SECTIONS = [OPENINGS, ENGAGE, ACCOUNT, COMPANY_SETTINGS]

export function pathOf(url: string): string {
  return url.split('?')[0].split('#')[0]
}

/**
 * @param url     the router's current URL
 * @param title   the deepest activated route's `data.title`
 * @param record  what the record page has published for this URL; absent while it is loading
 */
export function buildBreadcrumbs(url: string, title?: string, record?: RecordCrumbs | null): Crumb[] {
  const path = pathOf(url)

  const match = RECORDS.filter(r => path.startsWith(r.prefix) && !r.except?.some(e => path.startsWith(e))).sort(
    (a, b) => b.prefix.length - a.prefix.length
  )[0]
  if (match) {
    if (match.static) return [match.back, { label: title || 'Detail' }]
    if (!record) return []
    return [record.back ?? match.back, { label: record.title || title || 'Detail' }]
  }

  for (const section of SECTIONS) {
    const tab = section.tabs.filter(t => path.startsWith(t.prefix)).sort((a, b) => b.prefix.length - a.prefix.length)[0]
    if (!tab) continue
    // The landing tab IS the section here, so it does not get its own crumb.
    if (tab.isLanding) return [{ label: section.label }]
    return [{ label: section.label, link: section.landing, queryParams: section.landingQueryParams }, { label: tab.label }]
  }

  return title ? [{ label: title }] : []
}
